import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AccessToken, RefreshToken, UserClaims } from '@okta/okta-auth-js';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, tap } from 'rxjs/operators';

import * as authActions from './auth.actions';
import { AuthService } from './auth.service';

/**
 * Whether a rejected `session.refresh()` means the SESSION is gone, as opposed to
 * the request never having reached Okta.
 *
 * An HTTP status is the proof that Okta answered: `AuthApiError` carries the
 * response it sent, with the transport status on `xhr.status`. A refresh Okta
 * answered is authoritative whatever it said — 403/404 for an expired or absent
 * session, and any other refusal still means the endpoint was reached and said no,
 * which is not a state the client can go on rendering as "signed in".
 *
 * With NO status the call died in the transport — offline, DNS, a dropped
 * connection, a cancelled request — and that says nothing at all about the
 * session. Treating it as an ended session logs out a patient whose session is
 * almost certainly still valid, whose only mistake was clicking "Stay signed in"
 * on a flaky network (PR review 08/25). `xhr.status` is checked as a POSITIVE
 * number because that is the other shape a transport failure takes: a 0 status on
 * an `xhr` that exists but never got a response.
 *
 * Fail-OPEN, unlike the other gates around here: an error of an unrecognized
 * shape keeps the patient signed in. Guessing wrong that way costs the stale UI
 * QA filed on 08/23, and the next 401 from any call corrects it; guessing wrong
 * the other way throws away a live session and whatever the patient was doing in
 * it, with no way back but signing in again.
 */
function isOktaSessionEnded(error: unknown): boolean {
  const status = (error as { xhr?: { status?: number } } | null)?.xhr?.status;
  return typeof status === 'number' && status > 0;
}

@Injectable()
export class AuthEffects {
  authenticate$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.authenticate),
        exhaustMap((action) =>
          this.authService
            .login(
              action.email,
              action.password,
              action.redirect || '/portal/dashboard',
              action.context
            )
            .pipe(
              map(() => authActions.authenticateSuccess({})),
              catchError((_error) => of(authActions.authenticateFailure()))
            )
        )
      ),
    { dispatch: false }
  );

  logout = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.logOut),
      exhaustMap((action) =>
        this.authService.logoutAPI().pipe(
          map((_success) =>
            authActions.signOutOkta({ redirect: action.redirect })
          ),
          catchError((_error) =>
            of(authActions.logOutFailure({ redirect: action.redirect }))
          )
        )
      )
    )
  );

  logoutOkta = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.signOutOkta),
      exhaustMap((action) =>
        this.authService.logoutAllSessions().pipe(
          map((_success) =>
            authActions.logOutSuccess({ redirect: action.redirect })
          ),
          catchError((_error) =>
            of(authActions.logOutFailure({ redirect: action.redirect }))
          )
        )
      )
    )
  );

  redirectAfterLogout = createEffect(
    () =>
      this.actions$.pipe(
        ofType(authActions.logOutSuccess, authActions.logOutFailure),
        tap((action) => this.authService.redirect(action.redirect))
      ),
    { dispatch: false }
  );

  updateLoginInfo = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.updateLoginInfoSuccess),
      exhaustMap((action) =>
        this.authService.getUserInfo().pipe(
          map((_success: UserClaims) => {
            // Setting token value to space - empty string causes an error
            // We need other properties of token, such as expiration
            const curAccess =
              this.authService.oktaAuth.tokenManager.getTokensSync()
                .accessToken as AccessToken;
            const curRefresh =
              this.authService.oktaAuth.tokenManager.getTokensSync()
                .refreshToken as RefreshToken;

            this.authService.oktaAuth.tokenManager.setTokens({
              accessToken: {
                ...curAccess,
                accessToken: ' ',
              },
              refreshToken: {
                ...curRefresh,
                refreshToken: ' ',
              },
              idToken: undefined,
            });
            return authActions.authenticateSuccess({ response: _success });
          }),
          catchError((_error) =>
            of(authActions.updateLoginInfoFailure({ payload: action.payload }))
          )
        )
      )
    )
  );

  refreshSession = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.refreshSession),
      exhaustMap(() =>
        this.authService.refreshSession().pipe(
          map(() => authActions.refreshSessionSuccess()),
          catchError((error) =>
            of(
              authActions.refreshSessionFailure({
                sessionEnded: isOktaSessionEnded(error),
              })
            )
          )
        )
      )
    )
  );

  /**
   * A "Stay signed in" that OKTA refused ends the session (QA 08/23).
   *
   * `refreshSession` is dispatched from exactly one place — the idle-timeout
   * modal's Continue button, `handleSession(false)` in both layouts — and it
   * refreshes the OKTA session. When Okta rejects it the session is already gone
   * and there is nothing left to recover; until now nothing acted on the failure
   * either (no reducer case, no effect), so `auth.loggedIn` stayed TRUE for a
   * patient the server had stopped recognizing. The portal went on rendering as
   * signed in while every call behind it returned 401/403 — QA found it through
   * the AI agent, which kept offering its entry bar on the home page and then
   * answered the question with "You don't have access to the AI assistant right
   * now" (the 403 copy). Any other feature on the page was in the same state; the
   * agent is just the one that says so out loud.
   *
   * Gated on `sessionEnded` because "the refresh failed" and "the session is over"
   * are not the same fact (PR review 08/25). This effect used to act on EVERY
   * rejection, which made a dropped connection at the moment the patient clicked
   * Continue sign them out — the one click that says "I am still here" answered by
   * ending their session, over a network blip that never asked Okta anything. See
   * `isOktaSessionEnded` for where the line is drawn. A transport failure now
   * leaves the session exactly as it was, which is the truthful outcome: nothing
   * about it changed, and the modal's own timeout still ends it if the patient
   * really has gone.
   *
   * Routed through `logOut` rather than a new reducer case so the sign-out happens
   * once, in the place that already owns all of it: `loggedIn` and the user are
   * cleared, the feature slices reset (`orbiFeatureReducer` keys on this action),
   * the logout API and the Okta sessions are closed, and `redirectAfterLogout`
   * sends the patient to /landing — the same destination the modal's own timeout
   * path uses.
   */
  refreshSessionFailed = createEffect(() =>
    this.actions$.pipe(
      ofType(authActions.refreshSessionFailure),
      filter(({ sessionEnded }) => sessionEnded),
      map(() => authActions.logOut({ redirect: '/landing' }))
    )
  );

  constructor(private actions$: Actions, private authService: AuthService) {}
}
